import React from 'react';
import { Link } from 'react-router-dom';
import { 
  User, Mail, Type, FileText, Phone, MapPin, Hash, Calendar, 
  Clock, Link2, List, Radio, CheckSquare, ListChecks, Upload, 
  Code, X, Crown, PenTool, ShieldCheck
} from 'lucide-react';

// Field types configuration
export const fieldTypes = [
  // Basic Fields
  { type: 'name', label: 'Full Name', icon: User, category: 'basic' },
  { type: 'email', label: 'Email', icon: Mail, category: 'basic' },
  { type: 'text', label: 'Short Text', icon: Type, category: 'basic' },
  { type: 'textarea', label: 'Long Text', icon: FileText, category: 'basic' },
  { type: 'phone', label: 'Phone', icon: Phone, category: 'basic' },
  
  // Advanced Fields
  { type: 'address', label: 'Address', icon: MapPin, category: 'advanced' },
  { type: 'number', label: 'Number', icon: Hash, category: 'advanced' },
  { type: 'date', label: 'Date Picker', icon: Calendar, category: 'advanced' },
  { type: 'time', label: 'Time', icon: Clock, category: 'advanced' },
  { type: 'url', label: 'Website', icon: Link2, category: 'advanced' },
  
  // Choice Fields
  { type: 'select', label: 'Dropdown', icon: List, category: 'choice' },
  { type: 'radio', label: 'Single Choice', icon: Radio, category: 'choice' },
  { type: 'checkbox', label: 'Checkbox', icon: CheckSquare, category: 'choice' },
  { type: 'multiple', label: 'Multi Select', icon: ListChecks, category: 'choice' },
  
  // Upload & Special
  { type: 'file', label: 'File Upload', icon: Upload, category: 'upload' },
  { type: 'signature', label: 'Signature', icon: PenTool, category: 'special', premium: true },
  { type: 'heading', label: 'Heading', icon: Type, category: 'special' },
  { type: 'captcha', label: 'CAPTCHA', icon: ShieldCheck, category: 'special', description: 'Spam protection' },
];

// Dark sidebar colors that match Formlio's brand
export const sidebarStyles = {
  background: '#1E293B', // Dark slate
  text: '#E2E8F0',
  textMuted: '#94A3B8',
  accent: '#4F46E5', // Indigo
  accentHover: '#6366F1',
  categoryHeader: '#64748B',
  fieldHover: '#334155',
  fieldActive: '#4F46E5',
};

// Field Elements Sidebar Component
export const FieldElementsSidebar = ({ 
  onAddField, 
  onClose, 
  showClose = false,
  isPremiumUser = false 
}) => {
  const categories = [
    { id: 'basic', label: 'BASIC FIELDS' },
    { id: 'choice', label: 'CHOICE FIELDS' },
    { id: 'advanced', label: 'ADVANCED' },
    { id: 'upload', label: 'UPLOADS' },
    { id: 'special', label: 'SPECIAL' },
  ];

  return (
    <div 
      className="w-64 flex flex-col"
      style={{ 
        backgroundColor: sidebarStyles.background,
        height: 'calc(100vh - 200px)' // Account for header and branding banner
      }}
    >
      {/* Header */}
      <div className="p-4 border-b border-slate-700 flex items-center justify-between">
        <h3 className="font-semibold text-white text-sm">Form Elements</h3>
        {showClose && (
          <button 
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      
      {/* Field List */}
      <div className="flex-1 overflow-y-auto p-3 pb-32 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        <style>{`.scrollbar-hide::-webkit-scrollbar { display: none; }`}</style>
        {categories.map(category => {
          const categoryFields = fieldTypes.filter(f => f.category === category.id);
          if (categoryFields.length === 0) return null;
          
          return (
            <div key={category.id} className="mb-4">
              <p 
                className="text-[10px] font-bold tracking-wider mb-2 px-2"
                style={{ color: sidebarStyles.categoryHeader }}
              >
                {category.label}
              </p>
              <div className="space-y-1">
                {categoryFields.map(fieldType => {
                  const Icon = fieldType.icon;
                  const isLocked = fieldType.premium && !isPremiumUser;
                  
                  return (
                    <button
                      key={fieldType.type}
                      onClick={() => onAddField(fieldType.type)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left group"
                      style={{
                        color: sidebarStyles.text,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = sidebarStyles.fieldHover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" style={{ color: sidebarStyles.textMuted }} />
                      <span className="text-sm flex-1">{fieldType.label}</span>
                      {fieldType.premium && (
                        <span className="w-5 h-5 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                          <Crown className="w-3 h-3 text-white" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Branding Banner Component - Shows for all users
export const BrandingBanner = ({ userPlan = 'free', showUpgrade = true, isAuthenticated = false }) => {
  const canRemoveBranding = userPlan === 'pro';
  
  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-40"
      style={{ backgroundColor: sidebarStyles.background }}
    >
      {/* Upgrade notice banner - Yellow for non-pro users, only show for guests (not authenticated) */}
      {!canRemoveBranding && !isAuthenticated && (
        <div className="bg-amber-400 text-amber-900 text-center py-2 text-sm font-medium">
          If you want to remove Formlio branding, please{' '}
          <Link to="/pricing" className="underline font-bold">upgrade your account</Link>
        </div>
      )}
      
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/formlio_final_logo.png" alt="Formlio" className="h-7 w-7 object-contain rounded-md" />
          <span className="text-white font-semibold text-sm">
            Now create your own <span className="text-indigo-400">Formlio</span> — It's Free!
          </span>
        </div>
        
        <Link 
          to="/signup" 
          className="px-5 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          Create a Free Form
        </Link>
      </div>
    </div>
  );
};

// Form Builder Header Tabs
export const BuilderTabs = ({ activeTab, onTabChange, tabs }) => {
  return (
    <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
            activeTab === tab.id
              ? 'bg-white text-indigo-600 shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          {tab.icon && <tab.icon className="w-4 h-4 mr-2 inline" />}
          {tab.label}
        </button>
      ))}
    </div>
  );
};

export default {
  fieldTypes,
  sidebarStyles,
  FieldElementsSidebar,
  BrandingBanner,
  BuilderTabs,
};
